import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import type { SimpleSnackBar } from '@angular/material/snack-bar';
import { ToastrService } from 'ngx-toastr';
import type { ActiveToast } from 'ngx-toastr';
import {
  CbreSnackBarConfiguration,
  CbreSnackBarOptions,
  CbreToastrConfiguration,
  CbreToastrOptions,
  ComplexTranslation,
  mergeDefaults,
  mergeSnackBarDefaults,
  ToastrTypes,
  TranslationFunction,
} from './alerts.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export const CBRE_ALERTS_TRANSLATION_FUNCTION = new InjectionToken<TranslationFunction>(
  'CBRE_ALERTS_TRANSLATION_FUNCTION'
);

@Injectable({
  providedIn: 'root',
})
export class CbreAlertService {
  private canTranslate = true;
  constructor(
    private toastrService: ToastrService,
    private snackBar: MatSnackBar,
    @Optional() @Inject(CBRE_ALERTS_TRANSLATION_FUNCTION) private translationFunction: TranslationFunction,
    @Optional() @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    if (translationFunction == null) {
      this.canTranslate = false;
      this.logger.error('Cbre translation function not provided.');
    }
  }

  public showInfoSnackBar(message: string, options?: Partial<CbreSnackBarOptions>): MatSnackBarRef<SimpleSnackBar> {
    const finalOptions: CbreSnackBarConfiguration = mergeSnackBarDefaults(options);
    return this.snackBar.open(message, finalOptions.actionButton, finalOptions);
    // Not using custom component while design is ready
    // return this.snackBar.openFromComponent(CustomSnackComponent, {
    //   data: finalOptions,
    //   duration: finalOptions.duration,
    //   panelClass: [finalOptions.panelClass],
    // });
  }

  public showSuccessSnackBar(message, options?: Partial<CbreSnackBarOptions>): MatSnackBarRef<SimpleSnackBar> {
    return this.showInfoSnackBar(message, options);
  }

  public showErrorSnackBar(message, options?: Partial<CbreSnackBarOptions>): MatSnackBarRef<SimpleSnackBar> {
    return this.showInfoSnackBar(message, options);
  }

  public showWarningSnackBar(message, options?: Partial<CbreSnackBarOptions>): MatSnackBarRef<SimpleSnackBar> {
    return this.showInfoSnackBar(message, options);
  }

  public showErrorToast(message: string, title?: string, options?: Partial<CbreToastrOptions>): ActiveToast<any> {
    const toastrConfig: CbreToastrConfiguration = mergeDefaults(ToastrTypes.error, options);
    return this.showToast<any>(message, title, toastrConfig);
  }

  public showWarningToast(message: string, title?: string, options?: Partial<CbreToastrOptions>): ActiveToast<any> {
    const toastrConfig: CbreToastrConfiguration = mergeDefaults(ToastrTypes.warning, options);
    return this.showToast<any>(message, title, toastrConfig);
  }

  public showInfoToast(message: string, title?: string, options?: Partial<CbreToastrOptions>): ActiveToast<any> {
    const toastrConfig: CbreToastrConfiguration = mergeDefaults(ToastrTypes.info, options);
    return this.showToast<any>(message, title, toastrConfig);
  }

  public showSuccessToast(message: string, title?: string, options?: Partial<CbreToastrOptions>): ActiveToast<any> {
    const toastrConfig: CbreToastrConfiguration = mergeDefaults(ToastrTypes.success, options);
    return this.showToast<any>(message, title, toastrConfig);
  }

  private showToast<T>(message: string, title: string, options: Partial<CbreToastrConfiguration>): ActiveToast<T> {
    let result: ActiveToast<T>;
    if (options.useInlineContainer) {
      this.toastrService.overlayContainer = options.inlineContainer;
      result = this.toastrService.error(message, title, options.toastrConfig);
      this.toastrService.overlayContainer = undefined;
    } else {
      result = this.toastrService.error(message, title, options.toastrConfig);
    }

    return result;
  }

  public showInfoSnackBar_l10n(
    message: string | ComplexTranslation,
    options?: Partial<CbreSnackBarOptions>
  ): MatSnackBarRef<SimpleSnackBar> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showInfoSnackBar(finalMessage, options);
  }

  public showSuccessSnackBar_l10n(
    message: string | ComplexTranslation,
    options?: Partial<CbreSnackBarOptions>
  ): MatSnackBarRef<SimpleSnackBar> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showSuccessSnackBar(finalMessage, options);
  }

  public showErrorSnackBar_l10n(
    message: string | ComplexTranslation,
    options?: Partial<CbreSnackBarOptions>
  ): MatSnackBarRef<SimpleSnackBar> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showErrorSnackBar(finalMessage, options);
  }

  public showWarningSnackBar_l10n(
    message: string | ComplexTranslation,
    options?: Partial<CbreSnackBarOptions>
  ): MatSnackBarRef<SimpleSnackBar> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showWarningSnackBar(finalMessage, options);
  }

  public showErrorToast_l10n(
    message: string | ComplexTranslation,
    title?: string,
    options?: Partial<CbreToastrOptions>
  ): ActiveToast<any> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showErrorToast(finalMessage, title, options);
  }

  public showWarningToast_l10n(
    message: string | ComplexTranslation,
    title?: string,
    options?: Partial<CbreToastrOptions>
  ): ActiveToast<any> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showWarningToast(finalMessage, title, options);
  }

  public showInfoToast_l10n(
    message: string | ComplexTranslation,
    title?: string,
    options?: Partial<CbreToastrOptions>
  ): ActiveToast<any> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showInfoToast(finalMessage, title, options);
  }

  public showSuccessToast_l10n(
    message: string | ComplexTranslation,
    title?: string,
    options?: Partial<CbreToastrOptions>
  ): ActiveToast<any> {
    const finalMessage = this.getTranslatedMessage(message);
    return this.showSuccessToast(finalMessage, title, options);
  }

  private getTranslatedMessage(message: string | ComplexTranslation): string {
    let result;
    if (typeof message === 'string') {
      result = message;
    }
    if (this.canTranslate) {
      try {
        result = this.translationFunction(message);
      } catch (error) {
        this.logger.error(`${error}`);
      }
    }
    return result;
  }
}
